import React, { useState, useEffect, useCallback } from "react";
import { useIntl } from "react-intl";
import { useContext } from "react";
import { I18NContext } from "../i18n/i18nprovider";
import { SiteLangs } from "../types/enums";
import LinkBox from "../components/LinkBox";

export type NewsPageProps = {
  articleType?: "official_news" | "industry_updates" | "mcb_updates";
};

export type Article = {
  id: number;
  date: string;
  title: string;
  title_es?: string;
  title_pt?: string;
  content: string;
  content_es?: string;
  content_pt?: string;
  categories: string[];
  file: string;
};

export type PaginatedResponse = {
  current_page: number;
  data: Article[];
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  links: Array<{
    url: string | null;
    label: string;
    active: boolean;
  }>;
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number;
  total: number;
};

const NewsPage: React.FC<NewsPageProps> = ({ articleType }) => {
  const intl = useIntl();
  const { lang } = useContext(I18NContext);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationData, setPaginationData] = useState<PaginatedResponse | null>(null);

  // const BEARER_TOKEN =
  //   process.env.NODE_ENV === "production"
  //     ? "$2y$10$WkeE5VJudD.s4NuIOqBvwe0aUdsmhvOXlHMdCwqomiM4Kj4pYKo0S"
  //     : "$2y$10$I/F8CGPq25qmy9hpVrjdBO/RCcsKORbB4aoYHnSOQ7anJ0LSkGZPK";

  const BEARER_TOKEN = "$2y$10$dq4pBEpsLHetYjRSfWY3COPndWP/cQo7CUvS7IcX2uyCdNSI.egoK";

  const fetchNews = useCallback(async (page: number = 1) => {
    try {
      setLoading(true);
      setError(null);

      // API configuration
      const getApiUrl = (page: number = 1) => {
        const baseUrl = "https://www.mexicocb.com.mx/mcb/api/v1/news";
        
        const params = new URLSearchParams();
        params.append('page', page.toString());
        
        // Default to industry_updates if no articleType is provided
        const tag = articleType || 'industry_updates';
        params.append('tag', tag);
        
        return `${baseUrl}?${params.toString()}`;
      };

      const response = await fetch(getApiUrl(page), {
        method: "GET",
        headers: {
          Authorization: `Bearer ${BEARER_TOKEN}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData: PaginatedResponse = await response.json();
      
      setPaginationData(responseData);
      setArticles(responseData.data || []);
      setCurrentPage(responseData.current_page);
    } catch (err) {
      console.error("Error fetching news:", err);
      setError(err instanceof Error ? err.message : "Failed to fetch news");
    } finally {
      setLoading(false);
    }
  }, [articleType, BEARER_TOKEN]);

  useEffect(() => {
    fetchNews(1);
  }, [fetchNews]);

  // Helper function to get the appropriate content based on current language
  const getLocalizedContent = (article: Article): string => {
    switch (lang) {
      case SiteLangs.ES:
        return article.content_es || article.content;
      case SiteLangs.PT:
        return article.content_pt || article.content;
      case SiteLangs.EN:
      default:
        return article.content;
    }
  };

  // Helper function to get the appropriate title based on current language
  const getLocalizedTitle = (article: Article): string => {
    switch (lang) {
      case SiteLangs.ES:
        return article.title_es || article.title;
      case SiteLangs.PT:
        return article.title_pt || article.title;
      case SiteLangs.EN:
      default:
        return article.title;
    }
  };


  // Helper function to check if file is a video
  const isVideoFile = (fileUrl: string): boolean => {
    const videoExtensions = ['.mp4', '.webm', '.ogg', '.avi', '.mov', '.wmv', '.flv', '.mkv'];
    const lowercaseUrl = fileUrl.toLowerCase();
    return videoExtensions.some(ext => lowercaseUrl.includes(ext));
  };

  // Helper function to get the page title based on article type
  const getPageTitle = (): string => {
    // Default to industry_updates if no articleType is provided
    const currentType = articleType || 'industry_updates';
    
    switch (currentType) {
      case "official_news":
        return intl.formatMessage({ id: "official-nom" });
      case "industry_updates":
        return intl.formatMessage({ id: "industry-updates" });
      case "mcb_updates":
        return intl.formatMessage({ id: "mcb-updates" });
      default:
        return intl.formatMessage({ id: "industry-updates" });
    }
  };

  // Pagination handlers
  const handlePageChange = (page: number) => {
    if (page !== currentPage && page >= 1 && paginationData && page <= paginationData.last_page) {
      fetchNews(page);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (paginationData && currentPage < paginationData.last_page) {
      handlePageChange(currentPage + 1);
    }
  };

  if (loading) {
    return (
      <div className="w-full flex flex-col">
        <div className="content flex md:!flex-row gap-0">
          <div className="w-full md:w-3/5 md:pr-8">
            <h2 className="text-secondary font-bold text-xl mb-6">
              {getPageTitle()}
            </h2>
            <div className="flex items-center justify-center py-20">
              <div className="text-lg text-gray-600">Loading news...</div>
            </div>
          </div>
          <div className="w-full flex flex-col md:w-2/5 gap-2">
            <LinkBox
              href={"/news/official-nom"}
              label={intl.formatMessage({ id: "official-nom" })}
            />
            <LinkBox
              href={"/news/industry-updates"}
              label={intl.formatMessage({ id: "industry-updates" })}
            />
            <LinkBox
              href={"/news/mcb-updates"}
              label={intl.formatMessage({ id: "mcb-updates" })}
            />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full flex flex-col">
        <div className="content flex md:!flex-row gap-0">
          <div className="w-full md:w-3/5 md:pr-8">
            <h2 className="text-secondary font-bold text-xl mb-6">
              {getPageTitle()}
            </h2>
            <div className="flex items-center justify-center py-20">
              <div className="text-lg text-red-600">
                Error loading news: {error}
              </div>
            </div>
          </div>
          <div className="w-full flex flex-col md:w-2/5 gap-2">
            <LinkBox
              href={"/news/official-nom"}
              label={intl.formatMessage({ id: "official-nom" })}
            />
            <LinkBox
              href={"/news/industry-updates"}
              label={intl.formatMessage({ id: "industry-updates" })}
            />
            <LinkBox
              href={"/news/mcb-updates"}
              label={intl.formatMessage({ id: "mcb-updates" })}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col">
      <div className="content flex md:!flex-row gap-0">
        <div className="w-full md:w-3/5 md:pr-8">
          <h2 className="text-secondary font-bold text-xl mb-6">
            {getPageTitle()}
          </h2>

          {articles.length === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-lg text-gray-600">
                No news articles available.
              </div>
            </div>
          ) : (
            articles.map((article, index) => (
              <div
                key={article.id}
                className="w-full flex flex-col gap-4 mb-12"
              >
                {article.file && (
                  <>
                    {isVideoFile(article.file) ? (
                      <div className="w-full flex items-center justify-center">
                        <video
                          src={article.file}
                          controls
                          className="w-full max-h-72 rounded-lg"
                          preload="metadata"
                        >
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    ) : (
                      <div className="w-full bg-gray-200 flex items-center justify-center min-h-44">
                        <img
                          src={article.file}
                          alt={getLocalizedTitle(article)}
                          className="h-full w-full object-contain rounded-lg max-h-72"
                        />
                      </div>
                    )}
                  </>
                )}
                <h1 className="text-secondary font-bold text-2xl">
                  {getLocalizedTitle(article)}
                </h1>
                <div
                  className="nested-links"
                  dangerouslySetInnerHTML={{
                    __html: getLocalizedContent(article),
                  }}
                ></div>
                <div className="text-sm text-gray-500">
                  {new Date(article.date).toLocaleDateString()}
                </div>
              </div>
            ))
          )}
          
          {/* Pagination */}
          {paginationData && (
            <div className="flex items-center justify-center mt-8 space-x-2">
              <button
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  currentPage === 1
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-secondary text-white hover:bg-secondary/80'
                }`}
              >
                {intl.formatMessage({ id: "previous" })}
              </button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, paginationData.last_page) }, (_, i) => {
                  let pageNum: number;
                  if (paginationData.last_page <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= paginationData.last_page - 2) {
                    pageNum = paginationData.last_page - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-2 rounded-md text-sm font-medium ${
                        pageNum === currentPage
                          ? 'bg-secondary text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={handleNextPage}
                disabled={currentPage === paginationData.last_page}
                className={`px-3 py-2 rounded-md text-sm font-medium ${
                  currentPage === paginationData.last_page
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-secondary text-white hover:bg-secondary/80'
                }`}
              >
                {intl.formatMessage({ id: "next" })}
              </button>
              
              <div className="ml-4 text-sm text-gray-600">
                {intl.formatMessage({ id: "page" })} {currentPage} {intl.formatMessage({ id: "of" })} {paginationData.last_page}
              </div>
            </div>
          )}
        </div>
        <div className="w-full flex flex-col md:w-2/5 gap-2">
            <LinkBox
              href={"/news/official-nom"}
              label={intl.formatMessage({ id: "official-nom" })}
            />
            <LinkBox
              href={"/news/industry-updates"}
              label={intl.formatMessage({ id: "industry-updates" })}
            />
            <LinkBox
              href={"/news/mcb-updates"}
              label={intl.formatMessage({ id: "mcb-updates" })}
            />
        </div>
      </div>
    </div>
  );
};

export default NewsPage;
